// @flow

import React from "react";
import {useFormik} from "formik";
import {useObserver} from "mobx-react-lite";
import {Input, Form, Modal, Select} from "antd";
import * as yup from "yup";
import {useTranslation} from "react-i18next";

import {csuPropType, statusUpdatePropType} from "./CourierStatusUpdatePropTypes";
import {changeStatusList} from "../../../constants/constants";

export const CourierStatusUpdateModal = ({
    isVisible,
    setVisible,
    defaultStatus,
    updateStatus
}: csuPropType) => {
    const {t} = useTranslation(["courierStatusUpdateModal", "changeStatusList"]);
    const formik = useFormik({
        initialValues: {note: "", status: ""},
        validationSchema: yup.object().shape({
            note: yup.string().required(t("validation.required", {ns: "common"})),
            status: yup.string().required(t("validation.required", {ns: "common"}))
        }),
        onSubmit: async (values: statusUpdatePropType) => {
            await updateStatus(values);
            await resetForm();
        },
        enableReinitialize: true
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        resetForm,
        setFieldValue
    } = formik;

    return useObserver(() => (
        <React.Fragment>
            <Modal
                visible={isVisible}
                centered
                title={t("courierStatusUpdateModalTitle", {ns: "courierStatusUpdateModal"})}
                okText={t("modal.saveButton", {ns: "common"})}
                onOk={handleSubmit}
                cancelText={t("modal.cancelButton2", {ns: "common"})}
                data-testid="courierStatuUpdateModal"
                okButtonProps={{
                    disabled: isSubmitting || defaultStatus === values.status,
                    "data-testid": "courierStatuUpdateModal_submitButton"
                }}
                cancelButtonProps={{"data-testid": "courierStatuUpdateModal_cancelButton"}}
                onCancel={() => {
                    resetForm();
                    setVisible();
                }}
            >
                <Form autoComplete="off" labelCol={{span: 24}} wrapperCol={{span: 24}}>
                    <Form.Item
                        label={t("fields.status", {ns: "common"})}
                        validateStatus={errors.status && touched.status ? "error" : "success"}
                        help={errors.status && touched.status ? errors.status : null}
                    >
                        <Select
                            showSearch
                            name="status"
                            value={values.status}
                            onChange={value => setFieldValue("status", value)}
                            data-testid="courierStatusSelect"
                        >
                            {changeStatusList.map(status => (
                                <Select.Option
                                    key={status.value}
                                    value={status.value}
                                    data-testid="courierStatusSelect_option"
                                >
                                    {t(status.translation_key, {ns: "changeStatusList"})}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        colon={false}
                        label={`${t("fields.note", {ns: "courierStatusUpdateModal"})}:`}
                        labelAlign="left"
                        validateStatus={errors.note && touched.note ? "error" : "success"}
                        help={errors.note && touched.note ? errors.note : null}
                    >
                        <Input.TextArea
                            name="note"
                            value={values.note}
                            onChange={handleChange}
                            data-testid="courierStatusNote"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </React.Fragment>
    ));
};
